import { waPhone } from "@/lib/phone";
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Phone, ArrowRight, User, WhatsappLogo, CurrencyInr } from "@phosphor-icons/react";

/**
 * Drilldown modal shown when the user clicks the "Total Receivable" HeroCard.
 * Groups every open order with a positive balance by customer (mobile) and
 * lists them sorted by amount due — biggest debtors on top.
 */
export default function ReceivablesDialog({ open, onClose, onOpenOrder }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/analytics/receivables");
        if (!cancel) setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancel) setBusy(false);
      }
    })();
    return () => { cancel = true; };
  }, [open]);

  const openOrder = (id) => {
    onClose?.();
    setTimeout(() => onOpenOrder?.(id), 60);
  };

  const openWhatsApp = (party, e) => {
    e.stopPropagation();
    const m = String(party.mobile || "").replace(/\D/g, "");
    const phone = waPhone(m);
    const text =
      `*Shobhit Creation* — Payment reminder\n\n` +
      `Namaste ${party.customer_name} ji, aapka outstanding balance ₹${Number(party.balance).toLocaleString("en-IN")} hai ` +
      `(${party.order_count} order${party.order_count === 1 ? "" : "s"}). Kripya jald settle karein.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl bg-slate-900 border-slate-700 text-white rounded-sm max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="receivables-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// TOTAL RECEIVABLE</div>
          <DialogTitle className="font-heading text-2xl">Who owes you money</DialogTitle>
          <div className="flex items-baseline gap-4 flex-wrap mt-1">
            <div>
              <span className="text-[10px] font-mono-num tracking-widest text-slate-500">TOTAL DUE</span>
              <div className="font-heading text-2xl font-black font-mono-num text-orange-400">
                ₹{Number(data?.total_receivable ?? 0).toLocaleString("en-IN")}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-mono-num tracking-widest text-slate-500">FROM CUSTOMERS</span>
              <div className="font-heading text-2xl font-black font-mono-num text-cyan-300">
                {data?.customer_count ?? 0}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 -mr-1">
          {busy && <div className="text-slate-500 text-sm py-10 text-center">Loading…</div>}
          {!busy && data && data.parties.length === 0 && (
            <div className="text-slate-500 text-sm py-10 text-center flex flex-col items-center gap-2">
              <CurrencyInr size={26} className="text-slate-600" />
              Wah! Sab payments up to date hain — koi bakaya nahi.
            </div>
          )}
          {!busy && data && data.parties.length > 0 && (
            <div className="space-y-2">
              {data.parties.map((p) => (
                <div
                  key={p.mobile}
                  className="surface-2 rounded-sm border border-slate-800 overflow-hidden"
                  data-testid={`receivable-party-${p.mobile}`}
                >
                  <div className="flex items-center gap-3 p-3">
                    <div className="w-9 h-9 rounded-sm bg-orange-500/20 border border-orange-500/50 flex items-center justify-center text-orange-400 shrink-0">
                      <User size={16} weight="bold" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-100 font-semibold truncate">{p.customer_name || "—"}</div>
                      <a
                        href={`tel:${p.mobile}`}
                        className="text-cyan-300 font-mono-num text-xs flex items-center gap-1 mt-0.5 hover:text-cyan-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone size={11} /> {p.mobile}
                      </a>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-num text-orange-400 font-bold text-lg">
                        ₹{Number(p.balance).toLocaleString("en-IN")}
                      </div>
                      <div className="text-[10px] font-mono-num text-slate-500">
                        {p.order_count} order{p.order_count === 1 ? "" : "s"}
                      </div>
                    </div>
                    <button
                      onClick={(e) => openWhatsApp(p, e)}
                      className="shrink-0 w-8 h-8 rounded-sm bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/30 flex items-center justify-center"
                      title="Send WhatsApp reminder"
                      data-testid={`receivable-whatsapp-${p.mobile}`}
                    >
                      <WhatsappLogo size={14} weight="bold" />
                    </button>
                  </div>
                  <div className="border-t border-slate-800 bg-slate-900/40 divide-y divide-slate-800">
                    {p.orders.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => openOrder(o.id)}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-800/50 text-left"
                        data-testid={`receivable-order-${o.order_number}`}
                      >
                        <span className="font-mono-num text-orange-400 text-sm">#{o.order_number}</span>
                        <span className="text-[10px] font-mono-num tracking-widest text-slate-500 uppercase">
                          {String(o.status || "").replace(/_/g, " ")}
                        </span>
                        <span className="ml-auto font-mono-num text-slate-100 text-sm">
                          ₹{Number(o.balance).toLocaleString("en-IN")}
                        </span>
                        <ArrowRight size={12} className="text-slate-500" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
